import {
  listPractitioners,
  listPatients as apiListPatients,
  createProfile as apiCreateProfile,
} from "../../../api/profile/create_profile.js";
import { updateProfile as apiUpdateProfile } from "../../../api/profile/update_profile.js";
import api from "../../../api.js";

export async function fetchPractitionersFromServer() {
  const items = await listPractitioners();
  return Array.isArray(items) ? items : [];
}

export async function fetchAllPatientsFromServer() {
  const aggregated = [];
  const pageSizeServer = 200;
  let page = 1;
  let total = 0;

  for (; ;) {
    const { data, total: serverTotal } = await apiListPatients({
      search: "",
      page,
      pageSize: pageSizeServer,
    });
    const items = Array.isArray(data) ? data : [];
    aggregated.push(...items);
    total = Number(serverTotal || 0);
    if (!items.length || aggregated.length >= total) break;
    page += 1;
  }

  return aggregated;
}

export async function fetchPendingCreditApplicationsFromServer() {
  const res = await api.get("/api/public/credit-applications", {
    params: {
      status: "pending",
    },
  });

  const rows = Array.isArray(res?.data?.rows) ? res.data.rows : [];

  return rows.map((r) => {
    const fullName = String(r?.fullName || "").trim();
    const parts = fullName.split(/\s+/).filter(Boolean);
    const surname = parts.length ? parts[parts.length - 1] : "";
    const firstname = parts.length > 1 ? parts.slice(0, -1).join(" ") : "";

    return {
      id: `ca-${r?.applicationId}`,
      _type: "creditapplication",
      applicationId: r?.applicationId,
      firstname,
      surname,
      initial: (firstname || "").slice(0, 1).toUpperCase(),
      title: "",
      address: "",
      profile: null,
      _creditStatus: r?.status || "pending",
      _idNumber: r?.idNumber || "",
      _fullName: fullName,
    };
  });
}

export async function fetchAllPatientsAndPendingCreditsFromServer() {
  const [patients, pendingCredits] = await Promise.all([
    fetchAllPatientsFromServer(),
    fetchPendingCreditApplicationsFromServer(),
  ]);

  return [...patients, ...pendingCredits];
}

export async function saveProfileToServer({
  mode,
  selectedPatientId,
  mainMember,
  beneficiariesPayload,
  beneficiariesCount,
  enabledCount,
  profileEnabled,
  creditApplicationId,
}) {
  if (mode === "update" && selectedPatientId) {
    await apiUpdateProfile(selectedPatientId, {
      mainMember,
      beneficiaries: beneficiariesPayload,
      counts: {
        beneficiariesCount:
          typeof enabledCount === "number" ? enabledCount : beneficiariesCount,
      },
      profile: { enabled: profileEnabled },
    });
  } else {
    await apiCreateProfile({
      mainMember,
      beneficiaries: beneficiariesPayload,
      counts: { beneficiariesCount },
      creditApplicationId,
    });
  }
}

export async function handleSaveNewPatientHelper({
  isDirector,
  viewPersonal,
  viewMedicalAid,
  beneficiaries,
  conditionsList,
  allergiesList,
  modalMode,
  selectedPatientId,
  beneficiariesCount,
  creditApplicationId,
  setShowAddPatient,
  setIsViewMode,
  setSelectedPatientId,
  setCurrentPage,
  fetchAllPatients,
  showError,
}) {
  if (isDirector) return;

  const formatAddress = ({ street, suburb, city, postal }) => {
    const parts = [street, suburb, city, postal].filter(Boolean);
    return parts.join(", ");
  };

  const homeAddress = formatAddress({
    street: viewPersonal.homeStreet,
    suburb: viewPersonal.homeSuburb,
    city: viewPersonal.homeCity,
    postal: viewPersonal.homePostalCode,
  });
  const deliveryAddress = formatAddress({
    street: viewPersonal.deliveryStreet,
    suburb: viewPersonal.deliverySuburb,
    city: viewPersonal.deliveryCity,
    postal: viewPersonal.deliveryPostalCode,
  });

  const allergiesArray = (allergiesList || [])
    .map((s) => (s || "").trim())
    .filter(Boolean);
  const chronicArray = (conditionsList || [])
    .map((s) => (s || "").trim())
    .filter(Boolean);

  const mainIsIdType = (viewPersonal.idType || "id") === "id";
  const mainMember = {
    dependentCode: viewMedicalAid.dependentCode,
    firstname: viewPersonal.firstNames,
    surname: viewPersonal.surname,
    title: viewPersonal.title,
    idnumber: mainIsIdType ? (viewPersonal.idNumber || "") : null,
    passportnumber: !mainIsIdType ? (viewPersonal.idNumber || "") : null,

    dateofbirth: viewPersonal.dob || null,
    sex: viewPersonal.sex,
    age: viewPersonal.age,
    weightinkg: viewPersonal.weight,
    homeAddress,
    deliveryAddress,
    homeStreet: viewPersonal.homeStreet,
    homeSuburb: viewPersonal.homeSuburb,
    homeCity: viewPersonal.homeCity,
    homePostalCode: viewPersonal.homePostalCode,
    deliveryStreet: viewPersonal.deliveryStreet,
    deliverySuburb: viewPersonal.deliverySuburb,
    deliveryCity: viewPersonal.deliveryCity,
    deliveryPostalCode: viewPersonal.deliveryPostalCode,
    deliveryAddressesExtra: Array.isArray(viewPersonal.deliveryAddressesExtra)
      ? viewPersonal.deliveryAddressesExtra
      : [],
    allergies: allergiesArray,
    chronicconditions: chronicArray,
    email: viewPersonal.email,
    contactnumber: viewPersonal.cell,
    accountnumber: viewPersonal.accountNumber,
    employer: viewPersonal.employer,
    medicalaidprovider: viewMedicalAid.provider,
    medicalaidnumber: viewMedicalAid.medicalAidNumber,
    optioncodeid: viewMedicalAid.optioncodeid,
    practitionerid: viewMedicalAid.practitionerid,
    note: viewMedicalAid.note,
    enabled: viewPersonal.profileEnabled !== false,
  };

  const codes = [];
  const mainCode = (viewMedicalAid.dependentCode || "").trim();
  if (mainCode) codes.push({ who: "Main member", code: mainCode });
  (beneficiaries || []).forEach((b, i) => {
    const c = (b.dependentCode || "").trim();
    if (c) codes.push({ who: `Beneficiary #${i + 1}`, code: c });
  });
  const seen = new Set();
  for (const { who, code } of codes) {
    if (seen.has(code)) {
      showError(
        "Validation error",
        `Duplicate dependent code detected: ${code}. Please ensure all dependent codes are unique.`,
      );
      return;
    }
    seen.add(code);
  }

  {
    const hasIdOrPassport = !!(viewPersonal.idNumber || "").trim();
    const ageNum = Number(viewPersonal.age);
    const under18 = Number.isFinite(ageNum) && ageNum < 18;
    if (!hasIdOrPassport && under18 && !(viewPersonal.dob || "").trim()) {
      showError(
        "Main member validation",
        "Main Member: Date of Birth is required when no ID Number / Passport and age is under 18.",
      );
      return;
    }
  }

  for (let i = 0; i < (beneficiaries || []).length; i++) {
    const idx = i + 1;
    const b = beneficiaries[i] || {};
    const hasId = !!(b.idNumber || "").trim();
    const isIdType = (b.idType || "id") === "id";

    const ageNum = Number(b.age);
    const under18 = Number.isFinite(ageNum) && ageNum < 18;
    const eighteenOrMore = Number.isFinite(ageNum) && ageNum >= 18;

    const missing = [];
    if (!b.surname) missing.push("Surname");
    if (!b.firstNames) missing.push("First Name(s)");
    if (!b.title) missing.push("Title");
    if (!b.dob) missing.push("Date of Birth");
    if (!b.sex) missing.push("Sex");
    if (b.age === undefined || b.age === null || b.age === "") missing.push("Age");
    if (b.weight === undefined || b.weight === null || b.weight === "") missing.push("Weight (Kg)");
    if (!b.cell) missing.push("Home/Cell Number");
    if (!b.dependentCode) missing.push("Dependent Code");
    if (!b.homeStreet) missing.push("Home Street");
    if (!b.homeSuburb) missing.push("Home Suburb");
    if (!b.homeCity) missing.push("Home City");
    if (!b.homePostalCode) missing.push("Home Postal Code");
    if (!b.deliveryStreet) missing.push("Delivery Street");
    if (!b.deliverySuburb) missing.push("Delivery Suburb");
    if (!b.deliveryCity) missing.push("Delivery City");
    if (!b.deliveryPostalCode) missing.push("Delivery Postal Code");

    if (under18 && !hasId && !(b.dob || "").trim()) {
      showError(
        "Beneficiary validation",
        `Beneficiary #${idx}: Date of Birth is required when no ID Number / Passport and age is under 18.`,
      );
      return;
    }

    if (eighteenOrMore && isIdType) {
      const idVal = (b.idNumber || "").trim();
      if (!idVal || idVal.length !== 13) {
        showError(
          "Beneficiary ID validation",
          `Beneficiary #${idx}: Invalid South African ID number "${idVal || "(empty)"}". A valid 13-digit ID Number is required when age is 18 or older and ID Number is selected.`,
        );
        return;
      }
      if (b.dob) {
        const dobStr = String(b.dob).slice(0, 10);
        const yy = dobStr.slice(2, 4);
        const mm = dobStr.slice(5, 7);
        const dd = dobStr.slice(8, 10);
        const dobYYMMDD = `${yy}${mm}${dd}`;
        const idYYMMDD = idVal.slice(0, 6);
        if (dobYYMMDD !== idYYMMDD) {
          showError(
            "Beneficiary ID validation",
            `Beneficiary #${idx}: Date of Birth ${dobStr} does not match the first 6 digits of the South African ID number "${idVal}".`,
          );
          return;
        }
      }
    }

    if (missing.length) {
      showError(
        "Beneficiary validation",
        `Beneficiary #${idx}: Please fill in the following required fields: ${missing.join(", ")}.`,
      );
      return;
    }
  }

  const benPayload = (beneficiaries || []).map((b) => {
    const isIdType = (b.idType || "id") === "id";

    const bHome = formatAddress({
      street: b.homeStreet,
      suburb: b.homeSuburb,
      city: b.homeCity,
      postal: b.homePostalCode,
    });
    const bDelivery = formatAddress({
      street: b.deliveryStreet,
      suburb: b.deliverySuburb,
      city: b.deliveryCity,
      postal: b.deliveryPostalCode,
    });

    const bAllergies = Array.isArray(b.allergiesList)
      ? b.allergiesList.map((s) => (s || "").trim()).filter(Boolean)
      : (b.allergies || "").split(/[;,]/).map((s) => s.trim()).filter(Boolean);
    const bChronic = [b.conditions, ...(b.conditionsExtra || [])]
      .map((s) => (s || "").trim())
      .filter(Boolean);
    const enabled = b.enabled !== false;

    return {
      dependentCode: b.dependentCode,
      firstname: b.firstNames,
      surname: b.surname,
      title: b.title,
      idnumber: isIdType ? (b.idNumber || "") : null,
      passportnumber: !isIdType ? (b.idNumber || "") : null,
      dateofbirth: b.dob || null,
      sex: b.sex,
      age: b.age,
      weightinkg: b.weight,
      cell: b.cell,
      conditions: bChronic,
      allergies: bAllergies,
      homeAddress: bHome,
      deliveryAddress: bDelivery,
      deliveryStreet: b.deliveryStreet,
      deliverySuburb: b.deliverySuburb,
      deliveryCity: b.deliveryCity,
      deliveryPostalCode: b.deliveryPostalCode,
      deliveryAddressesExtra: Array.isArray(b.deliveryAddressesExtra)
        ? b.deliveryAddressesExtra
          .map((addr) => ({
            street: String(addr?.street || ""),
            suburb: String(addr?.suburb || ""),
            city: String(addr?.city || ""),
            postalCode: String(addr?.postalCode || addr?.postal || ""),
          }))
          .filter((addr) => [addr.street, addr.suburb, addr.city, addr.postalCode].some((v) => (v || "").trim()))
        : [],
      enabledbeneficiary: enabled,
    };
  });

  try {
    const enabledCount = (beneficiaries || []).filter((b) => b.enabled !== false).length;

    await saveProfileToServer({
      mode: modalMode,
      selectedPatientId,
      mainMember,
      beneficiariesPayload: benPayload,
      beneficiariesCount,
      enabledCount,
      profileEnabled: viewPersonal.profileEnabled !== false,
      creditApplicationId,
    });

    if (modalMode !== "update" && creditApplicationId) {
      try {
        await api.patch(
          `/api/public/credit-applications/${encodeURIComponent(creditApplicationId)}/status`,
          { status: "complete" }
        );
      } catch (_) {
      }
    }

    setShowAddPatient(false);
    setIsViewMode(false);
    setSelectedPatientId(null);
    setCurrentPage(1);
    fetchAllPatients();
  } catch (e) {
    const backendMessage =
      e?.response?.data?.message ||
      e?.response?.data?.error ||
      e?.message ||
      (modalMode === "update"
        ? "Network error while updating profile"
        : "Network error while creating profile");

    showError("Save failed", backendMessage);
  }
}